<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <BaseDialog
      :value="value"
      :title="editingMember ? 'Edit team member' : 'Add team member'"
      :disabled="invalid"
      :loading="isRequesting"
      @input="$emit('input', $event)"
      @confirm="saveMember"
      confirmLabel="Save"
      position="standard"
      class="team-members__dialog"
    >
      <div class="team-members__dialog-form">
        <div class="team-members__form-field">
          <label class="team-members__form-label">First Name</label>
          <ValidationProvider name="First Name" rules="required" v-slot="{ errors }">
            <q-input v-model="userData.firstName" outlined dense placeholder="Enter first name" class="team-members__form-input" :error="errors.length > 0" :error-message="errors[0]" />
          </ValidationProvider>
        </div>

        <div class="team-members__form-field">
          <label class="team-members__form-label">Last Name</label>
          <ValidationProvider name="Last Name" rules="required" v-slot="{ errors }">
            <q-input v-model="userData.lastName" outlined dense placeholder="Enter last name" class="team-members__form-input" :error="errors.length > 0" :error-message="errors[0]" />
          </ValidationProvider>
        </div>

        <div class="team-members__form-field">
          <label class="team-members__form-label">Email</label>
          <ValidationProvider :name="'Email Address'" :rules="editingMember ? '' : 'required|email'" v-slot="{ errors }">
            <q-input
              v-model="userData.email"
              outlined
              dense
              placeholder="Enter email address"
              :disable="!!editingMember"
              class="team-members__form-input"
              :error="errors.length > 0"
              :error-message="errors[0]"
            />
          </ValidationProvider>
        </div>

        <div class="team-members__form-field">
          <label class="team-members__form-label">Phone Number</label>
          <ValidationProvider name="Phone Number" rules="required|phone" v-slot="{ errors }">
            <BasePhoneNumber v-model="userData.phoneNumber" :error="errors.length > 0" :errorMessage="errors[0]" />
          </ValidationProvider>
        </div>

        <div class="team-members__form-field">
          <label class="team-members__form-label">Team Role</label>
          <BaseSelect v-model="userData.role" :options="roleOptions" :optionLabel="'label'" :optionValue="'value'" :clearable="false" />
        </div>
      </div>
    </BaseDialog>
  </ValidationObserver>
</template>

<script>
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';
import BasePhoneNumber from '@/components/base/BasePhoneNumber.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import { UserApi } from '@api/index';

// Extend validation rules
extend('required', {
  ...required,
  message: '{_field_} cannot be empty'
});

extend('email', {
  ...email,
  message: 'Please enter a valid email address',
  validate: async value => {
    if (!email.validate(value)) {
      return 'Please enter a valid email address';
    }

    try {
      const { statusCode, data } = await UserApi.checkEmailExistence(value);
      if (statusCode === 200 && data) {
        return !data.checker || 'This email is already in use. Please use a different email.';
      }
    } catch (error) {
      console.error('Error validating email:', error);
      return 'Unable to validate email. Please try again later.';
    }
  }
});

// Custom phone validation rule
extend('phone', {
  validate(value) {
    const phoneNumberRegex = new RegExp(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/);
    return phoneNumberRegex.test(value.replace(/\s/g, ''));
  },
  message: 'Invalid Phone Number'
});

export default {
  name: 'TeamMemberFormDialog',

  components: {
    ValidationObserver,
    ValidationProvider,
    BasePhoneNumber,
    BaseDialog,
    BaseSelect
  },

  props: {
    value: Boolean,
    editingMember: Object,
    isRequesting: Boolean
  },

  data() {
    return {
      userData: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: null // Will be set in created hook
      },
      roleOptions: [
        { label: 'Admin', value: 'manager' },
        { label: 'User', value: 'viewer' }
      ]
    };
  },

  created() {
    // Initialize with the role option object
    this.userData.role = this.roleOptions[1]; // Default to Viewer option object
  },

  watch: {
    editingMember(member) {
      if (member) {
        const firstName = member.name.split(' ')[0];
        const lastName = member.name.split(' ').slice(1).join(' ');
        const roleOption = this.roleOptions.find(option => option.value.toLowerCase() === (member.subClientRole || '').toLowerCase()) || this.roleOptions[1];

        this.userData = {
          firstName: firstName || '',
          lastName: lastName || '',
          email: member.email,
          phoneNumber: member.phone || '',
          role: roleOption
        };
      } else {
        this.userData = {
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          role: this.roleOptions[1]
        };
      }
    }
  },

  methods: {
    async saveMember() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;

      this.$emit('save', this.userData);
    }
  }
};
</script>

<style lang="scss" scoped>
@import './team-members-styles.scss';
</style>
